"use client";

/**
 * The full-page status screen: an outage (the proxy rewrites to
 * /system-status?code=service_down when it cannot reach Convex), a store
 * under review, rejected or paused — and the maintenance lock a Kuttos admin
 * switches on (MaintenanceScreen), which shows exactly the outage screen.
 * Moved here from app/system-status/page.tsx (Oct 2026) so both show the
 * same thing.
 */

import { useEffect, useState } from "react";
import { MAINTENANCE_COPY } from "@/lib/config/maintenance";

export type StatusCode = "service_down" | "brand_not_found" | "brand_pending" | "brand_rejected" | "brand_inactive";

interface StatusConfig {
  icon: React.ReactNode;
  title: string;
  message: string;
  subMessage?: string;
  primaryCta?: { label: string; action: "reload" };
  secondaryCta?: { label: string; action: "support" };
  autoRetry?: boolean;
  accentColor: string;
  accentGlow: string;
}

// ── SVG Icons ───────────────────────────────────────────────────────────────

function ShieldIcon({ color }: { color: string }) {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M28 4L8 14V26C8 39.2 16.4 51.4 28 54C39.6 51.4 48 39.2 48 26V14L28 4Z"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.3"
      />
      <path d="M28 4L8 14V26C8 39.2 16.4 51.4 28 54C39.6 51.4 48 39.2 48 26V14L28 4Z" fill={color} opacity="0.08" />
      {/* Wrench icon inside shield */}
      <path
        d="M33.5 22.5C33.5 20.84 32.66 19.37 31.38 18.5L33 16.88L31.12 15L29.5 16.62C28.63 16.22 27.65 16 26.62 16C22.78 16 19.62 19.16 19.62 23C19.62 26.84 22.78 30 26.62 30C27.65 30 28.63 29.78 29.5 29.38L36 35.88L37.88 34L31.38 27.5C32.66 26.63 33.5 25.16 33.5 23.5V22.5Z"
        fill={color}
        opacity="0.9"
      />
      <circle cx="26.5" cy="23" r="3" stroke={color} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function SearchIcon({ color }: { color: string }) {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="25" cy="25" r="12" stroke={color} strokeWidth="2.5" fill={color} fillOpacity="0.08" />
      <line x1="34" y1="34" x2="44" y2="44" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M20 25H30M25 20V30" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

function ClockIcon({ color }: { color: string }) {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="28" r="20" stroke={color} strokeWidth="2.5" fill={color} fillOpacity="0.08" />
      <path d="M28 16V28L36 32" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BlockIcon({ color }: { color: string }) {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="28" r="20" stroke={color} strokeWidth="2.5" fill={color} fillOpacity="0.08" />
      <line x1="14" y1="14" x2="42" y2="42" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function PauseIcon({ color }: { color: string }) {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="28" r="20" stroke={color} strokeWidth="2.5" fill={color} fillOpacity="0.08" />
      <rect x="21" y="18" width="5" height="20" rx="1.5" fill={color} opacity="0.8" />
      <rect x="30" y="18" width="5" height="20" rx="1.5" fill={color} opacity="0.8" />
    </svg>
  );
}

// ── Configurations ──────────────────────────────────────────────────────────

const STATUS_CONFIGS: Record<StatusCode, StatusConfig> = {
  service_down: {
    icon: <ShieldIcon color="#e879a0" />,
    title: MAINTENANCE_COPY.title,
    message: MAINTENANCE_COPY.message,
    subMessage: MAINTENANCE_COPY.subMessage,
    primaryCta: { label: "Reload App", action: "reload" },
    secondaryCta: { label: "Contact Support", action: "support" },
    autoRetry: true,
    accentColor: "#e879a0",
    accentGlow: "rgba(232,121,160,0.12)",
  },
  brand_not_found: {
    icon: <SearchIcon color="#f87171" />,
    title: "Store Not Found",
    message: "This store doesn't seem to exist. Double-check the URL and try again.",
    primaryCta: { label: "Reload App", action: "reload" },
    accentColor: "#f87171",
    accentGlow: "rgba(248,113,113,0.1)",
  },
  brand_pending: {
    icon: <ClockIcon color="#fbbf24" />,
    title: "Store Under Review",
    message: "This store is currently awaiting approval. Check back soon!",
    subMessage: "The store owner has been notified and the review process is underway.",
    accentColor: "#fbbf24",
    accentGlow: "rgba(251,191,36,0.1)",
  },
  brand_rejected: {
    icon: <BlockIcon color="#f87171" />,
    title: "Store Unavailable",
    message: "This store is not available at this time.",
    accentColor: "#f87171",
    accentGlow: "rgba(248,113,113,0.1)",
  },
  brand_inactive: {
    icon: <PauseIcon color="#94a3b8" />,
    title: "Store Is Paused",
    message: "This store is temporarily paused by its owner. Please check back later.",
    accentColor: "#94a3b8",
    accentGlow: "rgba(148,163,184,0.1)",
  },
};

/**
 * @param reloadOnRetry  The outage page reloads itself every 30 s to find the
 *   service back. The maintenance lock does not need to: it lifts by itself
 *   the moment it is switched off, and thousands of open tills reloading every
 *   30 s would cost real reads — so its countdown just starts over.
 * @param note  A line from us, under the message (the maintenance lock only).
 */
export function StatusScreen({
  code,
  sub,
  note,
  reloadOnRetry = true,
}: {
  code: StatusCode;
  sub?: string | null;
  note?: string | null;
  reloadOnRetry?: boolean;
}) {
  const config = STATUS_CONFIGS[code] ?? STATUS_CONFIGS.service_down;

  const [countdown, setCountdown] = useState(30);
  const [showTechnical, setShowTechnical] = useState(false);

  // Auto-retry countdown for service_down
  useEffect(() => {
    if (!config.autoRetry) return;
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          if (reloadOnRetry) window.location.reload();
          return 30;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [config.autoRetry, reloadOnRetry]);

  const handleReload = () => window.location.reload();

  const handleSupport = () => {
    window.open("https://wa.me/256780063058?text=Hi%2C%20I%20am%20experiencing%20issues%20accessing%20my%20store.", "_blank");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(160deg, #1a0a14 0%, #120812 40%, #0e0a12 100%)",
        fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
        padding: "24px",
        position: "relative",
        overflow: "hidden",
        color: "#e2e8f0",
      }}
    >
      {/* Background glow */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "700px",
          height: "500px",
          borderRadius: "50%",
          background: config.accentGlow,
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />

      {/* Main Card */}
      <div
        style={{
          position: "relative",
          maxWidth: "520px",
          width: "100%",
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "20px",
          padding: "48px 40px 36px",
          backdropFilter: "blur(16px)",
          textAlign: "center",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        {/* Icon */}
        <div style={{ marginBottom: "24px", display: "flex", justifyContent: "center" }}>{config.icon}</div>

        {/* Title */}
        <h1
          style={{
            fontSize: "22px",
            fontWeight: 700,
            color: config.accentColor,
            marginBottom: "12px",
            letterSpacing: "-0.3px",
            lineHeight: 1.3,
          }}
        >
          {config.title}
        </h1>

        {/* Message */}
        <p
          style={{
            fontSize: "14px",
            color: "#94a3b8",
            lineHeight: 1.7,
            marginBottom: config.subMessage || note ? "8px" : "28px",
            maxWidth: "400px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {config.message}
        </p>

        {/* A line from us (the maintenance lock) */}
        {note ? (
          <p
            style={{
              fontSize: "14px",
              color: "#e2e8f0",
              lineHeight: 1.6,
              margin: "16px auto 12px",
              maxWidth: "400px",
              padding: "10px 14px",
              borderRadius: "10px",
              background: "rgba(232,121,160,0.08)",
              border: "1px solid rgba(232,121,160,0.2)",
            }}
          >
            {note}
          </p>
        ) : null}

        {/* Sub-message */}
        {config.subMessage && (
          <p
            style={{
              fontSize: "12px",
              color: "#64748b",
              lineHeight: 1.6,
              marginBottom: "28px",
              maxWidth: "380px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {config.subMessage}
          </p>
        )}

        {/* Auto-retry progress bar */}
        {config.autoRetry && (
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                width: "100%",
                height: "3px",
                background: "rgba(255,255,255,0.06)",
                borderRadius: "3px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${((30 - countdown) / 30) * 100}%`,
                  background: `linear-gradient(90deg, ${config.accentColor}, ${config.accentColor}aa)`,
                  borderRadius: "3px",
                  transition: "width 1s linear",
                }}
              />
            </div>
            <p style={{ fontSize: "11px", color: "#475569", marginTop: "8px" }}>Auto-retrying in {countdown}s</p>
          </div>
        )}

        {/* Primary CTA */}
        {config.primaryCta && (
          <button
            onClick={config.primaryCta.action === "reload" ? handleReload : undefined}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              width: "100%",
              padding: "13px 24px",
              borderRadius: "10px",
              background: config.accentColor,
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              border: "none",
              cursor: "pointer",
              marginBottom: "12px",
              transition: "opacity 0.2s, transform 0.1s",
              boxShadow: `0 4px 20px ${config.accentColor}40`,
            }}
            onMouseOver={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M13.65 2.35A7.96 7.96 0 0 0 8 0C3.58 0 .01 3.58.01 8S3.58 16 8 16c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 8 14 6 6 0 1 1 8 2c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z"
                fill="white"
              />
            </svg>
            {config.primaryCta.label}
          </button>
        )}

        {/* Secondary CTA */}
        {config.secondaryCta && (
          <button
            onClick={config.secondaryCta.action === "support" ? handleSupport : undefined}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "11px 24px",
              borderRadius: "10px",
              background: "transparent",
              color: "#94a3b8",
              fontWeight: 500,
              fontSize: "13px",
              border: "1px solid rgba(255,255,255,0.08)",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
              e.currentTarget.style.color = "#e2e8f0";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
              e.currentTarget.style.color = "#94a3b8";
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {config.secondaryCta.label}
          </button>
        )}

        {/* View Technical Details */}
        <div style={{ marginTop: "20px" }}>
          <button
            onClick={() => setShowTechnical(!showTechnical)}
            style={{
              background: "none",
              border: "none",
              color: config.accentColor,
              fontSize: "12px",
              cursor: "pointer",
              opacity: 0.7,
              transition: "opacity 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
            onMouseOut={(e) => (e.currentTarget.style.opacity = "0.7")}
          >
            {showTechnical ? "Hide" : "View"} Technical Details
          </button>
        </div>

        {showTechnical && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px 16px",
              background: "rgba(0,0,0,0.3)",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.04)",
              textAlign: "left",
              fontSize: "11px",
              color: "#64748b",
              fontFamily: "'Consolas', 'Monaco', monospace",
              lineHeight: 1.8,
            }}
          >
            <div>
              Error Code: <span style={{ color: "#94a3b8" }}>{code}</span>
            </div>
            {sub && (
              <div>
                Domain: <span style={{ color: "#94a3b8" }}>{sub}</span>
              </div>
            )}
            <div>
              Timestamp: <span style={{ color: "#94a3b8" }}>{new Date().toISOString()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer info bar */}
      <div
        style={{
          position: "relative",
          maxWidth: "520px",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "24px",
          padding: "0 8px",
          fontSize: "11px",
          color: "#334155",
          fontFamily: "'Consolas', 'Monaco', monospace",
        }}
      >
        <span>Kuttos Technologies</span>
        <span>Status: Monitoring</span>
      </div>

      {/* Pulsing dots animation for service_down */}
      {code === "service_down" && (
        <div
          style={{
            marginTop: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: "5px",
                height: "5px",
                borderRadius: "50%",
                background: config.accentColor,
                opacity: 0.4,
                animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.4); }
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
      `}</style>
    </div>
  );
}

export default StatusScreen;
